import { RiskFactorToCancersImpactDto } from '@/api/generated/model';
import useIsMobile from '@/hooks/use-is-mobile';
import { formatRiskDecimal } from '@/utils/cancer/risk/format-risk-decimal';
import { formatCancerTypeNameWithCapitalization } from '@/utils/cancer/types/format-cancer-type-name-with-capitalization';
import { Box, Flex, Grid, IconArrowDown, IconArrowUp, IconInfoCircle, Popover, Text } from '@cmpsr/components';
import React from 'react';

export const RiskFactorBreakdownTableByCancer = ({ riskFactor }: { riskFactor: RiskFactorToCancersImpactDto }) => {
  const isMobile = useIsMobile();

  const stylesTableItem = (index?: number, array?: Array<unknown>) => {
    const isLast = index !== undefined && array !== undefined && index === array.length - 1;
    return {
      // Hide the border on the last item.
      // _last/@last-child won't work here because we are using a grid layout
      borderBottom: isLast ? 'none' : '1px solid',
      borderColor: 'ui-element-divider',
      p: 3,
    };
  };

  return (
    <Grid templateColumns="1fr auto" gap={0} mt={3}>
      <Text variant="text-body-floating-label-regular" {...stylesTableItem()}>
        Cancer
      </Text>
      <Text variant="text-body-floating-label-regular" textAlign="end" {...stylesTableItem()}>
        Impact{' '}
        <Popover trigger={isMobile ? 'click' : 'hover'} closeOnBlur placement="bottom">
          <Popover.Trigger>
            <IconInfoCircle size="xs" />
          </Popover.Trigger>
          <Popover.Content>
            <Popover.Arrow backgroundColor="text-secondary" />
            <Popover.Body
              backgroundColor="text-secondary"
              borderColor="transparent"
              borderRadius="radii-1"
              p="spacer-2"
            >
              <Text variant="text-body-meta-regular" color="text-light">
                This is the impact of this risk factor on your cancer risk relative to population average.
              </Text>
            </Popover.Body>
          </Popover.Content>
        </Popover>
      </Text>

      {riskFactor.cancers?.map((cancer, index, arr) => (
        <React.Fragment key={`${cancer.cancerType || index}`}>
          <Flex direction="column" justifyContent="center" {...stylesTableItem(index, arr)}>
            <Text variant="text-body-floating-label-bold" textTransform="capitalize">
              {formatCancerTypeNameWithCapitalization(cancer.cancerType)}
            </Text>
          </Flex>
          <Flex alignItems="center" justifyContent="flex-end" {...stylesTableItem(index, arr)}>
            <Box
              backgroundColor={cancer.impact < 0 ? 'alert-success-default' : 'alert-error-default'}
              px={2}
              py={1}
              borderRadius="radii-2"
              color={cancer.impact < 0 ? 'black' : 'white'}
              minWidth={'70px'}
              textAlign="center"
            >
              <Text variant="text-body-floating-label-bold" color="text-primary">
                {cancer.impact < 0 ? <IconArrowDown size="xs" /> : <IconArrowUp size="xs" />}{' '}
                {formatRiskDecimal(Math.abs(cancer.impact))}
              </Text>
            </Box>
          </Flex>
        </React.Fragment>
      ))}

      {(!riskFactor.cancers || riskFactor.cancers.length === 0) && (
        <Box gridColumn="span 2" p={4} textAlign="center" color="gray.500">
          No cancer impacts found for this risk factor.
        </Box>
      )}
    </Grid>
  );
};
